import { useMutation } from '@apollo/client';
import { Button } from '@c4ads/c4blocks';
import { Box, CircularProgress, TextField } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import apolloClient from '../../../../../api/apollo';
import { CREATE_CASE_FORM_COMMENT } from '../../../../../api/apollo/mutation/CreateCaseFormComment';
import { GET_CASE_FORM } from '../../../../../api/apollo/query';
import { useAuth } from '../../../../../api/auth';
import { CaseFormComment } from '../../../../../types/CaseFormComments';
import { FormSeparator } from '../components/FormSeparator';
import { Comment } from './Comment';
import styles from './Comments.module.css';

interface CommentsProps {
    id: string;
    comments: CaseFormComment[];
}

const prepareComments = (propComments: CaseFormComment[]) => {
    return propComments.sort((a, b) => {
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
};

export const Comments = ({ id, comments: propComments }: CommentsProps) => {
    const { user } = useAuth();
    const [comments, setComments] = useState<CaseFormComment[]>(prepareComments(propComments));
    const [commentText, setCommentText] = useState<string>();
    const [loading, setLoading] = useState(false);
    const [createCaseFormComment] = useMutation<{
        createCaseFormComment: {
            caseFormComment: CaseFormComment;
        };
    }>(CREATE_CASE_FORM_COMMENT);

    useEffect(() => {
        setComments(prepareComments(propComments));
    }, [propComments]);

    const onChangeComment = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setCommentText(e.target.value);
    }, []);

    const addComment = useCallback(async () => {
        setLoading(true);
        await createCaseFormComment({
            variables: {
                input: {
                    caseFormId: id,
                    text: commentText,
                },
            },
        });
        setCommentText('');
        await apolloClient.refetchQueries({
            include: [GET_CASE_FORM],
        });
        setLoading(false);
    }, [createCaseFormComment, id, commentText]);

    return user?.permissions?.map(({ codename }) => codename).includes('case_form_editor') ? (
        <Box display="flex" flexDirection="column" className={styles.container}>
            <Box display="flex" flexDirection="column" className={styles.textFieldContainer}>
                <TextField
                    className={styles.textField}
                    onChange={onChangeComment}
                    value={commentText}
                    label={null}
                    multiline
                    rows={6}
                    placeholder="Write your comment..."
                />
                <Button
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                    disabled={loading}
                    className={styles.addCommentButton}
                    color="primary"
                    variant="contained"
                    onClick={addComment}
                >
                    Add Comment
                </Button>
            </Box>
            <Box>
                {comments.length > 0 && <FormSeparator />}
                <Box display="flex" flexDirection="column" className={styles.commentsBlock}>
                    {comments.map((comment, index) => (
                        <React.Fragment key={comment.id}>
                            <Comment value={comment} />
                            {index !== comments.length - 1 && <FormSeparator />}
                        </React.Fragment>
                    ))}
                </Box>
            </Box>
        </Box>
    ) : null;
};
